/**
 * Calls to the Worker. The admin app reaches the API over HTTP only, building each path from the
 * route table in @lattice/core and reading answers as the response types declared beside it.
 *
 * A refusal comes back as `{ ok: false }` with the Worker's stable error code, never thrown, so
 * every screen decides what each refusal means to the person looking at it.
 */
import type {
  AdminPatientIdentity,
  AdminReviewViolation,
  RouteSpec,
  StaffRole,
} from "@lattice/core";
import { pathOf } from "@lattice/core";

export type Result<T> =
  | { readonly ok: true; readonly data: T }
  | {
      readonly ok: false;
      readonly status: number;
      readonly error: string;
      /** Whose record a `reason_required` refusal is about. Null with every other refusal. */
      readonly patient: AdminPatientIdentity | null;
      readonly violations: readonly AdminReviewViolation[] | null;
    };

interface Call {
  readonly params?: Readonly<Record<string, string>>;
  readonly query?: Readonly<Record<string, string>>;
  readonly body?: object;
  readonly headers?: Readonly<Record<string, string>>;
}

function urlFor(route: RouteSpec, call: Call): string {
  const path = pathOf(route.path, call.params ?? {});
  const query = new URLSearchParams(call.query ?? {}).toString();

  return query === "" ? path : `${path}?${query}`;
}

export async function api<T>(route: RouteSpec, call: Call = {}): Promise<Result<T>> {
  const headers = new Headers(call.headers);

  if (call.body !== undefined) headers.set("content-type", "application/json");

  const init: RequestInit =
    call.body === undefined
      ? { method: route.method, headers }
      : { method: route.method, headers, body: JSON.stringify(call.body) };

  const response = await fetch(urlFor(route, call), init).catch(() => null);

  if (response === null)
    return { ok: false, status: 0, error: "offline", patient: null, violations: null };

  if (!response.ok) {
    /* A proxy in front of the Worker can answer with an HTML error page instead of JSON. */
    const refusal: {
      readonly error?: string;
      readonly patient?: AdminPatientIdentity;
      readonly violations?: readonly AdminReviewViolation[];
    } | null = await response.json().catch(() => null);

    return {
      ok: false,
      status: response.status,
      error: refusal?.error ?? "unexpected",
      patient: refusal?.patient ?? null,
      violations: refusal?.violations ?? null,
    };
  }

  /*
    Not parsed field by field: the Worker builds this body from queries typed with the same core
    response types, and this app ships from the same commit, so the two agree on the shape.
  */
  const data: T | null = await response.json().catch(() => null);

  return data === null
    ? { ok: false, status: response.status, error: "unexpected", patient: null, violations: null }
    : { ok: true, data };
}

/** The signed-in caller, as /api/me answers. */
export interface Me {
  readonly userId: string;
  readonly staffRole: StaffRole | null;
  readonly email: string | null;
}
